import type { EventType } from "@fluid-app/fluid";

// Extended event types to include our custom events
export type ExtendedEventType = EventType | "CHECKOUT_INITIATED";

/**
 * Events for controlling the cart sheet
 */
export const CART_SHEET_EVENTS = {
  OPEN_CART: "FLUID_OPEN_CART",
  CLOSE_CART: "FLUID_CLOSE_CART",
  TOGGLE_CART: "FLUID_TOGGLE_CART",
  CART_LOADING_START: "CART_LOADING_START",
  CART_LOADING_END: "CART_LOADING_END",
} as const;

export type CartSheetEventType =
  (typeof CART_SHEET_EVENTS)[keyof typeof CART_SHEET_EVENTS];

/**
 * Dispatch a cart event with widget tracking information
 * @param eventType The type of event to dispatch
 * @param detail Additional event details
 * @param widgetId Unique identifier for the widget instance
 */
export const dispatchCartEvent = (
  eventType: ExtendedEventType,
  detail: Record<string, unknown> = {},
  widgetId: string,
): void => {
  window.dispatchEvent(
    new CustomEvent(eventType as string, {
      detail: {
        ...detail,
        handledByWidget: true,
        timestamp: new Date().toISOString(),
        widgetId, // Use the consistent widget ID
      },
    }),
  );
};

/**
 * Dispatch a cart sheet control event
 * @param eventType The type of cart sheet event to dispatch
 * @param detail Additional event details
 */
export const dispatchCartSheetEvent = (
  eventType: CartSheetEventType,
  detail: Record<string, unknown> = {},
): void => {
  window.dispatchEvent(
    new CustomEvent(eventType as string, {
      detail: {
        ...detail,
        timestamp: new Date().toISOString(),
      },
    }),
  );
};

/**
 * Generate a unique widget ID
 * @returns A unique string ID
 */
export const generateWidgetId = (prefix = "widget"): string => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};
